/**
 * Gomme magique : the brush the customer passes over what must go (the vanilla component of the
 * magic-eraser module, kept as it was : only its network half moved to the MagicEraser service,
 * which posts through fx.Ajax like every other call of the editor).
 *
 * To be used over the WHOLE photo (never the crop of the page) : the mask must cover the source
 * picture. The picture shown may be a light version, only its ratio matters : the server scales
 * the mask back to the full size.
 *
 * Circling something erases it too : once a stroke ends, whatever the strokes enclose is filled,
 * on screen and in the mask (_fillEnclosed). Customers outline the object, as in the phone
 * erasers they know ; a ring alone made Bria rebuild the very person it surrounded.
 *
 *   let brush = new shared.magiceraser.Brush({ image : imgElement, onStatus : (s) => ... });
 *   brush.setBrushSize(40);
 *   brush.undo(); brush.clear();
 *   brush.buildMaskDataURL().then((png) => ...);   // black, brushed area white
 *   brush.overlayDataURL();   // what is on screen, transparent around : a CSS mask of the area
 *   brush.bounds;             // { x0, y0, x1, y1 } of the strokes, 0..1 of the photo
 *   brush.destroy();
 */
fx.ns("shared.magiceraser", class Brush {

  constructor({ image, brushSize = 40, onStatus = () => {} }) {
    if (!image.complete || !image.naturalWidth) throw new Error("L'image doit être chargée");
    Object.assign(this, { image, onStatus });
    this.brushSize = brushSize;     // in screen px
    this.strokes = [];              // normalized coordinates 0..1
    this.current = null;
    this.maxMaskSide = 2048;        // the server scales the mask back to the original

    this.canvas = document.createElement('canvas');
    Object.assign(this.canvas.style, {
      position: 'absolute', left: '0', top: '0', cursor: 'none', touchAction: 'none',
    });
    // the tip of the brush, in place of the system cursor (mouse and pen only : a finger hides it)
    this.pointer = document.createElement('div');
    Object.assign(this.pointer.style, {
      position: 'absolute', width: '14px', height: '14px', margin: '-7px 0 0 -7px', boxSizing: 'border-box',
      border: '2px solid #fff', borderRadius: '50%', boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.4)',
      pointerEvents: 'none', display: 'none', zIndex: '1',
    });
    const parent = image.parentElement;
    if (getComputedStyle(parent).position === 'static') parent.style.position = 'relative';
    parent.appendChild(this.canvas);
    parent.appendChild(this.pointer);
    this.ctx = this.canvas.getContext('2d');

    this._onDown = this._onDown.bind(this);
    this._onMove = this._onMove.bind(this);
    this._onUp = this._onUp.bind(this);
    this._onLeave = this._onLeave.bind(this);
    this._resize = this._resize.bind(this);
    this.canvas.addEventListener('pointerdown', this._onDown);
    this.canvas.addEventListener('pointermove', this._onMove);
    this.canvas.addEventListener('pointerleave', this._onLeave);
    window.addEventListener('pointerup', this._onUp);
    this._ro = new ResizeObserver(this._resize);
    this._ro.observe(image);
    this._resize();
  }

  setBrushSize(px) { this.brushSize = px; }
  undo() { this.strokes.pop(); this._redraw(); this.onStatus('drawing'); }
  clear() { this.strokes = []; this._redraw(); this.onStatus('drawing'); }
  get isEmpty() { return this.strokes.length === 0; }

  // hidden while the result is compared, so a stray touch draws nothing
  set enabled(val) { this.canvas.style.display = val ? '' : 'none'; if (!val) this._onLeave(); }

  // on show but deaf to touches, while the erase runs
  set locked(val) { this.canvas.style.pointerEvents = val ? 'none' : ''; if (val) this._onLeave(); }

  get bounds() {
    let x0 = 1, y0 = 1, x1 = 0, y1 = 0;
    for (const s of this.strokes)
      for (const [x, y] of s.points) {
        x0 = Math.min(x0, x - s.size / 2); x1 = Math.max(x1, x + s.size / 2);
        y0 = Math.min(y0, y - s.size / 2); y1 = Math.max(y1, y + s.size / 2);
      }
    return this.isEmpty ? null : { x0: Math.max(0, x0), y0: Math.max(0, y0), x1: Math.min(1, x1), y1: Math.min(1, y1) };
  }

  overlayDataURL() { return this.canvas.toDataURL('image/png'); }

  /** Up to `count` random points inside what is drawn (filled area included), 0..1 of the photo. */
  samplePoints(count) {
    const { width: w, height: h } = this.canvas;
    const b = this.bounds;
    if (!w || !h || !b) return [];
    const data = this.ctx.getImageData(0, 0, w, h).data;
    const points = [];
    for (let tries = 0; points.length < count && tries < count * 50; tries++) {
      const x = b.x0 + Math.random() * (b.x1 - b.x0), y = b.y0 + Math.random() * (b.y1 - b.y0);
      const i = (Math.min(h - 1, Math.floor(y * h)) * w + Math.min(w - 1, Math.floor(x * w))) * 4;
      if (data[i + 3] > 127) points.push([x, y]);
    }
    return points;
  }

  destroy() {
    this._ro.disconnect();
    window.removeEventListener('pointerup', this._onUp);
    this.canvas.remove();
    this.pointer.remove();
  }

  /** The mask as a PNG data URI, the way the server reads it. */
  buildMaskDataURL() {
    return this._buildMask().then((blob) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    }));
  }

  // ---------- internal ----------

  _resize() {
    const { offsetWidth: w, offsetHeight: h, offsetLeft: x, offsetTop: y } = this.image;
    const dpr = window.devicePixelRatio || 1;
    Object.assign(this.canvas.style, { width: `${w}px`, height: `${h}px`, left: `${x}px`, top: `${y}px` });
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
    this._redraw();
  }

  _point(e) {
    const r = this.canvas.getBoundingClientRect();
    return [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height];
  }

  _onDown(e) {
    this.canvas.setPointerCapture(e.pointerId);
    // thickness normalized by the displayed width => independent of the zoom
    this.current = { size: this.brushSize / this.canvas.getBoundingClientRect().width, points: [this._point(e)] };
    this.strokes.push(this.current);
    this.onStatus('drawing');
    this._redraw();
  }

  _onMove(e) {
    this._follow(e);
    if (!this.current) return;
    this.current.points.push(this._point(e));
    this._redraw();
  }

  _follow(e) {
    if (e.pointerType === 'touch') { this._onLeave(); return; }
    const r = this.canvas.getBoundingClientRect();
    Object.assign(this.pointer.style, {
      display: 'block',
      left: `${this.canvas.offsetLeft + e.clientX - r.left}px`,
      top: `${this.canvas.offsetTop + e.clientY - r.top}px`,
    });
  }

  _onLeave() { this.pointer.style.display = 'none'; }

  _onUp() {
    if (!this.current) return;
    this.current = null;
    this._redraw();   // now that the stroke is over, what it encloses shows as erased too
  }

  _drawStrokes(ctx, w, h, color) {
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    for (const s of this.strokes) {
      const lw = s.size * w;
      if (s.points.length === 1) {
        const [x, y] = s.points[0];
        ctx.beginPath(); ctx.arc(x * w, y * h, lw / 2, 0, Math.PI * 2); ctx.fill();
        continue;
      }
      ctx.lineWidth = lw;
      ctx.beginPath();
      s.points.forEach(([x, y], i) => (i ? ctx.lineTo(x * w, y * h) : ctx.moveTo(x * w, y * h)));
      ctx.stroke();
    }
  }

  _redraw() {
    const { width: w, height: h } = this.canvas;
    this.ctx.clearRect(0, 0, w, h);
    // opaque strokes then a global opacity : no highlight where they cross
    this.ctx.globalAlpha = 1;
    this._drawStrokes(this.ctx, w, h, 'rgb(255, 64, 129)');
    // filling while a stroke is drawn would cost a full scan per pointer move
    if (!this.current) this._fillEnclosed(this.ctx, w, h, [255, 64, 129]);
    this.canvas.style.opacity = '0.55';
  }

  /** Binary PNG mask : black background, area to erase in white, same ratio as the photo. */
  _buildMask() {
    const { naturalWidth: nw, naturalHeight: nh } = this.image;
    const k = Math.min(1, this.maxMaskSide / Math.max(nw, nh));
    const c = document.createElement('canvas');
    c.width = Math.round(nw * k);
    c.height = Math.round(nh * k);
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, c.width, c.height);
    this._drawStrokes(ctx, c.width, c.height, '#fff');
    this._fillEnclosed(ctx, c.width, c.height, [255, 255, 255]);
    return new Promise((resolve) => c.toBlob(resolve, 'image/png'));
  }

  /**
   * Paints in `rgb` every pixel the strokes enclose : a flood fill from the border over what is
   * not painted marks the outside, anything unpainted left over is a hole. Works on the display
   * canvas (transparent background) and on the mask (black background) alike, a pixel counting
   * as painted when it is opaque and bright in red.
   */
  _fillEnclosed(ctx, w, h, rgb) {
    if (!w || !h || this.isEmpty) return;

    const image = ctx.getImageData(0, 0, w, h);
    const data = image.data;
    const total = w * h;
    const outside = new Uint8Array(total);   // 1 = painted or reached from the border
    const stack = new Int32Array(total);
    let top = 0;

    for (let i = 0; i < total; i++)
      if (data[i * 4 + 3] > 127 && data[i * 4] > 127) outside[i] = 1;

    const push = (i) => { if (!outside[i]) { outside[i] = 1; stack[top++] = i; } };
    for (let x = 0; x < w; x++) { push(x); push((h - 1) * w + x); }
    for (let y = 0; y < h; y++) { push(y * w); push(y * w + w - 1); }

    while (top) {
      const i = stack[--top];
      const x = i % w;
      if (x > 0) push(i - 1);
      if (x < w - 1) push(i + 1);
      if (i >= w) push(i - w);
      if (i < total - w) push(i + w);
    }

    let filled = false;
    for (let i = 0; i < total; i++) {
      // the stroke's own edge made solid : half transparent, it drew a ring inside the fill
      if (outside[i]) { if (data[i * 4 + 3] > 127) data[i * 4 + 3] = 255; continue; }
      const o = i * 4;
      data[o] = rgb[0]; data[o + 1] = rgb[1]; data[o + 2] = rgb[2]; data[o + 3] = 255;
      filled = true;
    }

    if (filled) ctx.putImageData(image, 0, 0);
  }
});
